import { authenticatedFetch } from "@utils/authenticatedFetch";
import { getApiOrigin } from "@utils/apiOrigin";

export interface PermissionFeature {
  feature_id: number | string;
  facility_id?: number | string;
  view: boolean;
  add: boolean;
  update: boolean;
  delete: boolean;
}

export interface SendEmailAddUserPayload {
  facility_id: number;
  first_name: string;
  last_name: string;
  email: string;
  user_type_id: number;
  user_subtype_id?: number;
  bypass_confirmation: boolean;
  is_affable_bpm_user?: boolean;
  permissions?: PermissionFeature[];
  org_name?: string;
}

export interface SendEmailAddUserError {
  code: string;
  message: string;
}

export interface AffableBpmSyncDetails {
  status_code?: number;
  payload?: Record<string, unknown> | null;
  message?: string | null;
}

export interface AffableBpmSyncResult {
  success: boolean;
  details?: AffableBpmSyncDetails | null;
  error?: string | null;
}

export interface SendEmailAddUserResponse {
  success: boolean;
  message: string;
  email_sent: boolean | null;
  user_created: boolean | null;
  user_id: number | null;
  association_id: number | null;
  temporary_password: string | null;
  confirmation_token: string | null;
  error?: SendEmailAddUserError | null;
  status_code?: number;
  affable_bpm_sync?: AffableBpmSyncResult | null;
}

const API_BASE_URL = getApiOrigin();
const SEND_EMAIL_ADD_USER_PATH =
  "/api/v1/user-management/send-email-add-user";
const GET_FACILITY_USERS_PATH = (facilityId: number) =>
  `/api/v1/user-management/facility/${facilityId}/users`;
const CONFIRM_INVITATION_PATH =
  "/api/v1/user-management/confirm-invitation";
const DECRYPT_DATA_PATH = "/api/v1/user-management/decrypt-data";
const GET_USER_TYPES_PATH = "/api/v1/user-types";
const CHECK_USER_EXISTS_PATH = "/api/v1/user-management/check-user-exists";

export async function sendEmailAddUser(
  payload: SendEmailAddUserPayload,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<SendEmailAddUserResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(`${baseUrl}${SEND_EMAIL_ADD_USER_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as SendEmailAddUserResponse) : null;

  if (!response.ok) {
    const errorMessage = json?.message || `Request failed with ${response.status}`;
    const error = new Error(errorMessage);
    throw error;
  }

  return json as SendEmailAddUserResponse;
}

export interface PendingInvitation {
  id: number;
  facility_id: number;
  email: string;
  first_name: string;
  last_name: string;
  user_type: string;
  invited_by_user_id: number;
  confirmation_token: string;
  is_confirmed: boolean;
  expires_at: string;
  created_at: string;
}

export interface FacilityUsersResponse {
  success: boolean;
  message: string;
  users: any[];
  pending_invitations: PendingInvitation[];
  count: number;
  error?: any;
}

export async function getFacilityUsers(
  facilityId: number,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<FacilityUsersResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(`${baseUrl}${GET_FACILITY_USERS_PATH(facilityId)}`, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as FacilityUsersResponse) : null;

  if (!response.ok) {
    const errorMessage = json?.message || `Request failed with ${response.status}`;
    const error = new Error(errorMessage);
    throw error;
  }

  return json as FacilityUsersResponse;
}

export interface User {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  userType: string;
}

export async function getUserList(): Promise<User[]> {
  // TODO: Replace with actual API call to get current facility ID
  const facilityId = 1; // This should come from user context or facility selection

  try {
    const response = await getFacilityUsers(facilityId);

    // Transform the API response to match the User interface
    const users: User[] = response.users.map((user: any) => ({
      id: user.id?.toString() || '',
      firstName: user.first_name || '',
      lastName: user.last_name || '',
      email: user.email || '',
      userType: user.user_type || "Standard User"
    }));

    return users;
  } catch (error) {
    console.error('Error fetching user list:', error);
    throw error;
  }
}

export interface ConfirmInvitationPayload {
  confirmation_token: string;
  password: string;
}

export interface ConfirmInvitationResponse {
  success: boolean;
  message: string;
  user_id: number | null;
  association_id: number | null;
  access_token: string | null;
  error?: { code: string; message: string } | null;
}

export interface DecryptDataResponse {
  success: boolean;
  message: string;
  data: Record<string, any> | null;
  error?: { code?: string; message?: string } | null;
}

export interface UserSubtype {
  id: number;
  name: string;
  user_type_id?: number;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
}

export interface UserType {
  id: number;
  name: string;
  is_active: boolean;
  is_deleted: boolean;
  created_at: string;
  updated_at: string;
  user_subtypes: UserSubtype[];
}

export interface UserTypesResponse {
  success: boolean;
  message: string;
  data: {
    items: UserType[];
    total: number;
    page: number;
    page_size: number;
  };
}

export interface GetUserTypesOptions {
  page?: number;
  page_size?: number;
  filters?: Record<string, any>;
  baseUrlOverride?: string;
  accessTokenOverride?: string;
}

export interface ExistingUserData {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  display_name?: string;
  full_name?: string;
  is_active?: boolean;
  is_verified?: boolean;
  is_admin?: boolean;
  user_type_id?: number;
  user_subtype_id?: number | null;
  created_at?: string;
  updated_at?: string;
}

export interface UserFacilityAssociation {
  id: number;
  user_id: number;
  facility_id: number;
  role?: string;
  access_level?: string;
  is_primary?: boolean;
  is_active?: boolean;
  org_name?: string;
  created_at?: string;
  updated_at?: string;
}

export interface CheckUserExistsResponse {
  success: boolean;
  message: string;
  exists: boolean;
  data: ExistingUserData | null;
  assigned_to_facility?: boolean;
  association?: UserFacilityAssociation | null;
  error?: { code?: string; message?: string } | null;
}

export async function checkUserExistsByEmail(
  email: string,
  facilityId?: number,
  options?: { baseUrlOverride?: string; accessTokenOverride?: string }
): Promise<CheckUserExistsResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(`${baseUrl}${CHECK_USER_EXISTS_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      email: email.trim().toLowerCase(),
      ...(facilityId ? { facility_id: facilityId } : {}),
    }),
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as CheckUserExistsResponse) : null;

  if (!response.ok) {
    const errorMessage = json?.message || `Request failed with ${response.status}`;
    const error = new Error(errorMessage);
    throw error;
  }

  return json as CheckUserExistsResponse;
}

/**
 * Confirm a pending invitation by setting password with a confirmation token.
 * Note: This endpoint typically does NOT require an Authorization header.
 */
export async function confirmInvitation(
  payload: ConfirmInvitationPayload,
  options?: { baseUrlOverride?: string }
): Promise<ConfirmInvitationResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const response = await fetch(`${baseUrl}${CONFIRM_INVITATION_PATH}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      // No bearer token by design
    },
    body: JSON.stringify(payload),
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as ConfirmInvitationResponse) : null;

  if (!response.ok) {
    const errorMessage = json?.message || `Request failed with ${response.status}`;
    const error = new Error(errorMessage);
    (error as any).response = json;
    throw error;
  }

  return json as ConfirmInvitationResponse;
}

/**
 * Decrypt invitation payload using Rules API (no auth required).
 */
export async function decryptInvitationData(
  encryptedData: string,
  options?: { baseUrlOverride?: string }
): Promise<Record<string, any>> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  const response = await fetch(`${baseUrl}${DECRYPT_DATA_PATH}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ encrypted_data: encryptedData }),
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as DecryptDataResponse) : null;

  if (!response.ok || !json?.success || !json?.data) {
    const errorMessage =
      json?.error?.message || json?.message || `Request failed with ${response.status}`;
    throw new Error(errorMessage);
  }

  return json.data;
}

/**
 * Fetch user types from the API with optional pagination and filters.
 * @param options - Optional parameters for pagination, filters, and overrides
 * @returns Promise<UserTypesResponse> - The user types response
 */
export async function getUserTypes(
  options?: GetUserTypesOptions
): Promise<UserTypesResponse> {
  const baseUrl = options?.baseUrlOverride || API_BASE_URL || "";
  if (!baseUrl) {
    throw new Error(
      "REACT_APP_API_BASEURL is not set. Set it to your backend origin in .env."
    );
  }

  // Build query parameters
  const params = new URLSearchParams();
  if (options?.page !== undefined) {
    params.append("page", options.page.toString());
  }
  if (options?.page_size !== undefined) {
    params.append("page_size", options.page_size.toString());
  }
  if (options?.filters) {
    params.append("filters", JSON.stringify(options.filters));
  }

  const queryString = params.toString();
  const url = `${baseUrl}${GET_USER_TYPES_PATH}${queryString ? `?${queryString}` : ""}`;

  const fetchFn = options?.accessTokenOverride
    ? (u: string, o: RequestInit) =>
        fetch(u, {
          ...o,
          headers: {
            ...Object.fromEntries(new Headers(o.headers).entries()),
            Authorization: `Bearer ${options.accessTokenOverride}`,
          },
        })
    : authenticatedFetch;

  const response = await fetchFn(url, {
    method: "GET",
    headers: { "Content-Type": "application/json" },
  });

  const text = await response.text();
  const json = text ? (JSON.parse(text) as UserTypesResponse) : null;

  if (!response.ok) {
    const errorMessage = json?.message || `Request failed with ${response.status}`;
    const error = new Error(errorMessage);
    throw error;
  }

  return json as UserTypesResponse;
}

const userManagementService = {
  sendEmailAddUser,
  getFacilityUsers,
  getUserList,
  confirmInvitation,
  decryptInvitationData,
  getUserTypes,
  checkUserExistsByEmail,
};

export default userManagementService;


